import { useRecoilValue } from 'recoil';
import {
  type DeviceHeader,
  deviceModeAtom,
  showClientIdentifierSelector,
} from '../../../models/atoms/device_atoms';
import {
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { DevicesTableEntry } from './DevicesTableEntry';
import { DevicesFilter } from './DevicesFilter';
import { sortedDevicesSelector } from '../../../models/selectors/device_selectors';
import { DevicesTableHead } from './DevicesTableHead';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { DeviceInfoCard } from './DeviceInfoCard';
import { formatDeviceKey } from '../../../utils/deviceKey';
import { isFetchingDeviceListAtom } from '../../../models/atoms/ui_atoms';

export const ALERT_LABELS = [
  'Low battery',
  'No messages',
  'High ROC',
  'Blockage',
  'High Level',
  'blockage',
  'flood',
  'silt',
  'infiltration',
];

export type TableHeader = {
  label: string;
  key: DeviceHeader;
  sort: boolean;
};

const liveHeaders: TableHeader[] = [
  { label: 'Device ID', key: 'device_id', sort: true },
  { label: 'Fill %', key: 'current_level_percentage', sort: true },
  { label: 'Labels/Alerts', key: 'labels', sort: false },
  { label: 'Pinned', key: 'pinned', sort: false },
];

const archivedHeaders: TableHeader[] = [
  { label: 'Device ID', key: 'device_id', sort: true },
  { label: 'Start Date', key: 'creation_date', sort: true },
  { label: 'End Date', key: 'end_date', sort: true },
  { label: 'Labels/Alerts', key: 'labels', sort: false },
  { label: 'Pinned', key: 'pinned', sort: false },
];

export const DeviceListComponent = () => {
  const sortedDevices = useRecoilValue(sortedDevicesSelector);
  const showClientIdentifier = useRecoilValue(showClientIdentifierSelector);
  const isMobile = useIsMobile();
  const mode = useRecoilValue(deviceModeAtom);
  const isFetchingDeviceList = useRecoilValue(isFetchingDeviceListAtom);

  const headers = mode === 'live' ? [...liveHeaders] : [...archivedHeaders];

  if (showClientIdentifier) {
    headers.splice(1, 0, {
      label: 'Client ID',
      key: 'client_identifier',
      sort: true,
    });
  }

  return (
    <Paper
      component="article"
      sx={{ height: '100%' }}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
      }}
    >
      <Stack
        data-testid="Device List Component"
        sx={{
          pt: isMobile ? '0' : '1rem',
          borderRadius: '5px',
          height: '100%',
        }}
      >
        <DevicesFilter />

        {isFetchingDeviceList[mode] ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        ) : isMobile ? (
          <Stack gap={2} sx={{ maxHeight: '100%', overflow: 'auto' }}>
            {sortedDevices.map(device => (
              <DeviceInfoCard
                key={formatDeviceKey(device.device_id, device.session_id)}
                device={device}
              />
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <DevicesTableHead headers={headers} />

              <TableBody>
                {sortedDevices.map(device => (
                  <DevicesTableEntry
                    key={formatDeviceKey(device.device_id, device.session_id)}
                    columns={headers.map(h => h.key)}
                    device={device}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
};
